/**
 * AI cars, cyclists, pedestrians, spawning, avoidance, collision reaction.
 *
 * OWNER: `traffic` — ARCHITECTURE.md §3.
 *
 * WHAT THIS SUBSYSTEM IS FOR
 *   The city has to be alive and driving fast has to be dangerous. Traffic is the moving
 *   half of both. Every car is a readable block travelling at roughly half your top speed, so
 *   you are permanently closing on something; every cyclist is a 1.85 m gap you have to
 *   decide about; every pedestrian is a reminder that the road is not yours.
 *
 * PUBLISHED INTERFACE (§4.9b) — `ctx.traffic`
 *   vehicles()  cyclists()  pedestrians()  nearest(x, z, maxDist)  count
 *   plus, for `gameplay`: entities() and patchHits().
 *   Returned objects are LIVE references. Read them, do not mutate them.
 *
 * EVENTS EMITTED (§5.1, authoritative)
 *   traffic:nearMiss { position, gap, speed, kind }
 *   traffic:spooked  { kind, position }
 *   traffic:hit      { kind, position, impulse }
 *
 * EVENTS CONSUMED
 *   world:weatherChanged — headlights on/off.
 *   vehicle:collided     — a static crash rattles nearby traffic. The truck↔traffic contact
 *                          itself is detected HERE, because `vehicle` only collides against
 *                          static world geometry. We push the truck back through
 *                          `ctx.player.applyImpulse(x, y, z, 'traffic')`, which is what makes
 *                          `vehicle` emit the canonical `vehicle:collided` a step later.
 *
 * FRAME ORDER NOTE. `traffic` is in FIXED_ORDER but NOT in UPDATE_ORDER (src/core/registry.js),
 * so `update()` is effectively never called. All instance matrices are therefore written at
 * the end of `fixedUpdate()`, which is a pure function of simulation state — that satisfies
 * §7.3 (freezeClocks) for free, at the cost of not interpolating traffic between fixed steps.
 * At 60 Hz sim that is invisible. If core ever adds `traffic` to UPDATE_ORDER, `update()`
 * re-pushes the same pure-function presentation and stays correct.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';
import { LaneGraph } from './lanes.js';
import { buildModels, disposeModels } from './models.js';
import { TrafficRig, paintColours } from './render.js';
import { Fleet } from './agents.js';
import { ParkedField } from './parked.js';
import { clamp, clamp01 } from '../core/mathx.js';

const T = TUNING;
const V = T.visual;

export class TrafficSystem {
  static id = 'traffic';

  constructor(ctx) {
    this.ctx = ctx;
    this.rng = null;
    this.graph = null;
    this.models = null;
    this.rig = null;
    this.fleet = null;
    this.parked = null;
    this.query = null;

    this._built = false;
    this._night = 0;
    this._nightTarget = 0;
    this._weather = 'thaw';
    this._frozen = false;
    this._unsub = [];
    this._camPos = new THREE.Vector3();
  }

  /* ══════════════════════════════════════════════════════════════════ boot ══ */

  async init() {
    this.rng = this.ctx.rng.fork(T.rngLabel);
    await this._build();
    this._subscribe();
    this._publish();
  }

  async _build() {
    const ctx = this.ctx;

    this.graph = new LaneGraph(ctx.world);
    this.models = buildModels();
    this.rig = new TrafficRig(ctx, this.models);
    this.fleet = new Fleet(ctx, this.graph, this.rng, this.rig, this.models.kindNames);

    // §6.9. Static, built once, on its OWN rng fork so that changing the kerb layout can
    // never perturb the spawn stream and re-roll every moving car in every shot.
    this.parked = new ParkedField(
      ctx, this.graph, this.rng.fork('parked'),
      this.models.parked, this.rig.paint.mat, this.rig.tyre.mat, paintColours()
    );
    this.parked.build();
    this.fleet.bindParked(this.parked);

    // Bind the truck impulse sink once so the collision path never branches (§4.10).
    const player = ctx.player;
    if (typeof player.applyImpulse === 'function') {
      this.fleet.bindPlayerShove((x, y, z, kind) => player.applyImpulse(x, y, z, kind));
    }

    this._weather = ctx.gameplay.weather || 'thaw';
    this._nightTarget = this._weather === 'nightrain' ? 1 : 0;
    this._night = this._nightTarget;

    this.fleet.bulkFill(player.position.x, player.position.z);
    this.fleet._publish();
    this._built = true;

    // The first rendered frame happens before any fixed step, so push a pose now or the
    // fleet appears stacked at the origin for one frame.
    this._present();
  }

  _subscribe() {
    const bus = this.ctx.bus;
    const on = (t, fn) => { this._unsub.push(bus.on(t, fn)); };

    // `look` drives the visual, `gameplay` owns the state. Traffic only needs to know
    // whether the headlights should be on.
    on('world:weatherChanged', (e) => {
      if (!e) return;
      this._weather = e.weather;
      this._nightTarget = e.weather === 'nightrain' ? 1 : 0;
    });

    // A crash into static geometry rattles nearby traffic. Cheap, and it sells the impact.
    on('vehicle:collided', (e) => {
      if (!e || !this._built || e.other !== 'static') return;
      const p = e.point;
      if (!p) return;
      const list = this.fleet.activeCars;
      for (let i = 0; i < list.length; i++) {
        const c = list[i];
        const dx = c.x - p.x, dz = c.z - p.z;
        if (dx * dx + dz * dz < 90) c.hopV = Math.min(0.35, e.impulse * 0.02);
      }
    });
  }

  _publish() {
    const fleet = this.fleet;
    const self = this;
    this.query = {
      isFallback: false,
      get count() { return fleet.allActive.length; },
      vehicles() { return fleet.activeCars; },
      cyclists() { return fleet.activeCyclists; },
      pedestrians() { return fleet.activePeds; },
      nearest(x, z, maxDist) { return fleet.nearest(x, z, maxDist); },

      /**
       * Every active traffic entity in one iterable — cars, cyclists and pedestrians.
       * This is the handle `gameplay` should poll for "did something drive over my patch".
       */
      entities() { return fleet.allActive; },

      /**
       * Pothole ids whose FRESH (uncured) patch a traffic vehicle drove across during the
       * step that just finished. `gameplay` owns `patch:ruined`; traffic only reports.
       * Valid until the next fixed step.
       */
      patchHits() { return fleet.patchHits; },

      /** Debug/statistics surface. Not part of §4.9b. */
      _stats() { return self.harnessStats(); },
    };
    this.ctx.traffic = this.query;
  }

  /* ═══════════════════════════════════════════════════════════════════ sim ══ */

  fixedUpdate(dt) {
    if (!this._built) return;

    // Night blend, driven from sim time only, so shots stay bit-identical.
    const w = this.ctx.gameplay.weather;
    if (w && w !== this._weather) {
      this._weather = w;
      this._nightTarget = w === 'nightrain' ? 1 : 0;
    }
    const rate = 1.6 * dt;
    this._night += clamp(this._nightTarget - this._night, -rate, rate);

    this.fleet.step(dt);
    this._present();
  }

  /**
   * Presentation is a pure function of simulation state, so it is safe to call from either
   * hook. See the frame-order note at the top of the file.
   */
  update() {
    if (!this._built) return;
    this._present();
  }

  /* ═════════════════════════════════════════════════════════════ presentation ══ */

  _present() {
    const rig = this.rig;
    const fleet = this.fleet;
    const sim = this.ctx.time.sim;
    this._camPos.copy(this.ctx.camera.position);
    const cx = this._camPos.x, cz = this._camPos.z;
    const cull2 = V.cullDist * V.cullDist;
    const spin2 = V.wheelSpinDist * V.wheelSpinDist;
    const shadow2 = V.shadowDist * V.shadowDist;

    const SK = V.streak;
    const streak2 = SK.dist * SK.dist;
    const streakMin2 = SK.minDist * SK.minDist;
    const streakOn = this._night > 0.02;

    rig.syncMaterials();
    rig.setPoolOpacity(V.poolOpacity * this._night);
    rig.setStreakOpacity(this._night);
    rig.begin();

    // Static kerb field: one squared-distance test per tile to pick its LOD. Nothing else
    // about the parked field touches the frame.
    if (this.parked) this.parked.updateLod(cx, cz);

    const lampsOn = this._night > 0.05;

    // ── cars
    const cars = fleet.activeCars;
    for (let i = 0; i < cars.length; i++) {
      const c = cars[i];
      const dx = c.x - cx, dz = c.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 > cull2) continue;
      const model = T.models[c.kind];
      const rigM = this.models.kinds[c.kind].rig;
      const cy = Math.cos(c.yaw), sy = Math.sin(c.yaw);

      rig.vehicle(c.kindIdx, c.x, c.y, c.z, c.yaw, c.pitch, c.roll, c.colour);

      // ── contact shadow. Pinned to the GROUND sample and yaw only, so it stays welded to
      //    the tarmac while the body pitches, rolls and hops above it.
      if (d2 < shadow2) {
        rig.blob(c.x, c.ySample, c.z, c.yaw,
          model.W + V.shadowPadX * 2, model.L + V.shadowPadZ * 2);
      }

      // ── wheels. The FRONT pair (entries 0 and 1) carry the steering angle: in a still
      //    frame a turned wheel is one of only two cues that a car is being driven.
      const spin = d2 < spin2 ? c.wheelSpin : 0;
      for (let k = 0; k < rigM.wheels.length; k++) {
        const w = rigM.wheels[k];
        rig.wheel(c.x + sy * w[2] + cy * w[0], c.y + w[1], c.z + cy * w[2] - sy * w[0],
          k < 2 ? c.yaw + c.steer : c.yaw, spin, model.wheelR, model.wheelW);
      }
      if (rigM.extraAxle) {
        const oz = rigM.extraAxle;
        for (let s = -1; s <= 1; s += 2) {
          const ox = s * rigM.wheelX;
          rig.wheel(c.x + sy * oz + cy * ox, c.y + model.wheelR, c.z + cy * oz - sy * ox,
            c.yaw, spin, model.wheelR, model.wheelW);
        }
      }

      // ── lamps.
      //
      // The head and tail LENSES are drawn in every weather, not just at night. Round 8's
      // `combo-hud` fatal — "no glass, no lights, no grille" — was literally true of a
      // daylight frame: a moving car carried no lamp geometry at all, while the parked cars
      // beside it did. They are small by day (`dayHeadScale` / `dayTailScale`) so they read
      // as lenses catching the sun rather than as headlights switched on at noon.
      const headSize = lampsOn ? V.headSize : V.headSize * V.dayHeadScale;
      for (let s = -1; s <= 1; s += 2) {
        const ox = s * rigM.headX, oz = rigM.headZ;
        rig.lampHead(c.x + sy * oz + cy * ox, c.y + rigM.headY, c.z + cy * oz - sy * ox,
          c.yaw, headSize);
      }
      if (lampsOn) {
        // One fake light pool per car. This is the "cheap fake light cone" — 40 real lights
        // would cost more than the entire rest of the subsystem.
        const fz = rigM.headZ + V.poolForward;
        rig.lightPool(c.x + sy * fz, c.y + V.poolHeight, c.z + cy * fz, c.yaw, V.poolWidth, V.poolLength);
      }

      const tailSize = V.tailSize
        * (c.braking ? V.brakeTailScale : (lampsOn ? V.nightTailScale : V.dayTailScale));
      for (let s = -1; s <= 1; s += 2) {
        const ox = s * rigM.tailX, oz = rigM.tailZ;
        rig.lampTail(c.x + sy * oz + cy * ox, c.y + rigM.tailY, c.z + cy * oz - sy * ox,
          c.yaw + Math.PI, tailSize);
      }

      // ── wet-road light trails (§9.3). Ground-pinned to the SAMPLE, never to the body: the
      //    reflection lives on the tarmac and must not pitch, roll or hop with the car.
      if (streakOn && d2 < streak2 && d2 > streakMin2) {
        this._streak(c, dx, dz, d2, c.halfW * SK.lampSpread, c.braking ? 1.55 : 1);
      }

      if (c.turnDir !== 0 && c.planned) {
        const blink = ((sim / T.car.blinkPeriod + c.blinkOff) % 1) < 0.5;
        if (blink) {
          const s = c.turnDir;
          rig.lampInd(
            c.x + sy * rigM.indZ + cy * (s * rigM.indX), c.y + rigM.indY,
            c.z + cy * rigM.indZ - sy * (s * rigM.indX), c.yaw, V.indSize
          );
          rig.lampInd(
            c.x + sy * rigM.indRearZ + cy * (s * rigM.indRearX), c.y + rigM.indRearY,
            c.z + cy * rigM.indRearZ - sy * (s * rigM.indRearX), c.yaw + Math.PI, V.indSize
          );
        }
      }
    }

    // ── cyclists
    const cyc = fleet.activeCyclists;
    const bikeRig = this.models.cyclist.rig;
    const bike = T.bike;
    for (let i = 0; i < cyc.length; i++) {
      const c = cyc[i];
      const dx = c.x - cx, dz = c.z - cz;
      const cd2 = dx * dx + dz * dz;
      if (cd2 > cull2) continue;
      rig.cyclist(c.x, c.y, c.z, c.yaw, c.roll, c.colour);
      if (cd2 < shadow2) rig.blob(c.x, c.ySample, c.z, c.yaw, 0.72, bike.base + 0.7);
      const cy = Math.cos(c.yaw), sy = Math.sin(c.yaw);
      const spin = c.wobblePhase - (c.speed * sim) / bike.wheelR;
      for (let k = 0; k < 2; k++) {
        const oz = k === 0 ? bike.base * 0.5 : -bike.base * 0.5;
        rig.wheel(c.x + sy * oz, c.y + bike.wheelR, c.z + cy * oz, c.yaw, spin,
          bike.wheelR, bike.wheelW);
      }
      if (lampsOn) {
        rig.lampTail(c.x + sy * bikeRig.tailZ, c.y + bikeRig.tailY, c.z + cy * bikeRig.tailZ,
          c.yaw + Math.PI, V.indSize);
      }
      // A bike's rear lamp is a fraction of a car's, so its trail is proportionally short.
      if (streakOn && cd2 < streak2 && cd2 > streakMin2) {
        this._streak(c, dx, dz, cd2, 0, 0.42, true);
      }
    }

    // ── pedestrians
    const peds = fleet.activePeds;
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      const dx = p.x - cx, dz = p.z - cz;
      const pd2 = dx * dx + dz * dz;
      if (pd2 > cull2) continue;
      rig.ped(p.x, p.y, p.z, p.yaw, p.lean, p.arch, p.legPose,
        p.coat, p.trouser, p.skin, p.scaleXZ, p.scaleY);
      // ── contact shadow, on the GROUND sample so the walk bob cannot lift it off the
      //    pavement. Pedestrians were the only entity class without one of these; see the
      //    note on `blobMesh` in render.js for the measurement.
      if (pd2 < shadow2) {
        rig.blob(p.x, p.ySample, p.z, p.yaw,
          V.pedShadowW * p.scaleXZ, V.pedShadowL * p.scaleXZ);
      }
    }

    rig.commit();
  }

  /**
   * One wet-road reflection trail for agent `a` — reference/REFERENCE.md §9.3.
   *
   * `dx`/`dz` are (agent − camera), so (−dx, −dz) is the direction the reflection must run:
   * from the lamp's contact point TOWARD the eye. See `TUNING.visual.streak` for why that,
   * and not the car's own axis, is the only orientation that reads as a reflection.
   *
   * Which lamp you see is decided by `facing = forward · toCamera`: positive means the camera
   * is ahead of the car and sees its headlights (a long white trail); negative means it is
   * behind and sees the tail lamps (a short saturated red one). Allocation-free — everything
   * here is scalar arithmetic on values the caller already had.
   */
  _streak(a, dx, dz, d2, spread, gainScale, forceTail) {
    const SK = V.streak;
    const d = Math.sqrt(d2);
    const toX = -dx / d, toZ = -dz / d;
    const yaw = Math.atan2(toX, toZ);

    const fwdX = Math.sin(a.yaw), fwdZ = Math.cos(a.yaw);
    const facing = fwdX * toX + fwdZ * toZ;
    const head = !forceTail && facing > 0;

    // |facing| fades a side-on car's trail without ever killing it — a wet road still carries
    // some signature from a lamp raking across it.
    const k = SK.facingFloor + (1 - SK.facingFloor) * Math.abs(facing);
    const baseLen = head ? SK.headLen : SK.tailLen;
    const len = Math.min(baseLen * (1 + d * SK.lenPerMetre * 0.05), d * SK.reachFrac);
    const width = head ? SK.headWidth : SK.tailWidth;
    const gain = (head ? SK.headGain : SK.tailGain) * k * gainScale;

    // The lamp pair is offset across the car; put the trail under the lamp nearest the eye so
    // it is not hidden by the bodywork from a low chase camera (the §4.8b occlusion trap).
    const rX = toZ, rZ = -toX;                    // right-hand normal of the view ray
    const sx = a.x + rX * spread, sz = a.z + rZ * spread;
    this.rig.streak(sx, a.ySample, sz, yaw, width, len, head ? SK.headRgb : SK.tailRgb, gain);
  }

  onResize() {}

  /* ═══════════════════════════════════════════════════════════════ harness ══ */

  /** ARCHITECTURE.md §7.1. Apply only the keys traffic owns. */
  applyHarnessState(patch) {
    if (!patch || !this._built) return;

    if (patch.weather) {
      this._weather = patch.weather;
      this._nightTarget = patch.weather === 'nightrain' ? 1 : 0;
      this._night = this._nightTarget;          // snap: a shot has no time to blend
    }
    if (patch.timeOfDay != null && !patch.weather) {
      // 0.5 = noon. Anything past dusk turns the headlights on.
      const t = patch.timeOfDay;
      this._nightTarget = (t > 0.80 || t < 0.20) ? 1 : 0;
      this._night = this._nightTarget;
    }
    if (patch.trafficDensity != null) {
      const k = clamp01(patch.trafficDensity);
      this.fleet.targetCars = Math.round(T.population.maxCars * k);
      this.fleet.targetCyclists = Math.round(T.population.maxCyclists * k);
      this.fleet.targetPeds = Math.round(T.population.maxPeds * k);
    }

    // A scenario or a forced pose teleports the truck. Refill around the new position, or the
    // shot opens on an empty street.
    if (patch.playerPose || patch.scenario) {
      const p = this.ctx.player.position;
      const px = patch.playerPose && patch.playerPose.position ? patch.playerPose.position[0] : p.x;
      const pz = patch.playerPose && patch.playerPose.position ? patch.playerPose.position[2] : p.z;
      this.fleet.clear();
      this.fleet.bulkFill(px, pz);
      this.fleet._lastPlayerX = px;
      this.fleet._lastPlayerZ = pz;
      this.fleet._havePlayer = true;
      this.fleet._publish();
    }
    this._present();
  }

  /** Diagnostics only — how many visible cars are showing a brake lamp / a turned wheel. */
  _countBraking() {
    const l = this.fleet.activeCars;
    let n = 0;
    for (let i = 0; i < l.length; i++) if (l[i].braking) n++;
    return n;
  }

  _countSteered() {
    const l = this.fleet.activeCars;
    let n = 0;
    for (let i = 0; i < l.length; i++) if (Math.abs(l[i].steer) > 0.06) n++;
    return n;
  }

  harnessStats() {
    if (!this._built) return { built: false };
    const f = this.fleet;
    return {
      cars: f.activeCars.length,
      cyclists: f.activeCyclists.length,
      peds: f.activePeds.length,
      total: f.allActive.length,
      targets: [f.targetCars, f.targetCyclists, f.targetPeds],
      edges: this.graph.edgeCount,
      nodes: this.graph.nodeCount,
      bodies: this.rig.drawn.bodies,
      wheels: this.rig.drawn.wheels,
      lamps: this.rig.drawn.lamps,
      pools: this.rig.drawn.pools,
      streaks: this.rig.drawn.streaks,
      blobs: this.rig.drawn.blobs,
      braking: this._countBraking(),
      steered: this._countSteered(),
      night: +this._night.toFixed(3),
      weather: this._weather,
      frozen: this._frozen,
      spawned: f.stats.spawned,
      despawned: f.stats.despawned,
      hits: f.stats.hits,
      nearMisses: f.stats.nearMisses,
      spooks: f.stats.spooks,
      /**
       * MUST STAY 0. The count of agents recycled for having a non-finite position, and the
       * full state of the last one. See `Fleet._quarantineBroken()`.
       *
       * This is in `harness.stats()` on purpose. The round-12 NaN-pedestrian bug destroyed the
       * frame during PLAY and was invisible to all 42 captures, because a capture pins the
       * camera. Anyone who can reproduce it now gets the culprit's edge/s/lat/speed/state
       * printed for free instead of having to instrument the sim to find it.
       */
      quarantined: f.stats.quarantined,
      lastQuarantine: f._lastQuarantine,
      patchHits: f.patchHits.length,
      parked: this.parked ? this.parked.count : 0,
      parkedTiles: this.parked ? this.parked.tiles.length : 0,
      parkedMeshes: this.parked ? this.parked.meshes.length : 0,
      parkedStats: this.parked ? this.parked.stats : null,
    };
  }

  /**
   * §7.3. Everything traffic draws is already a pure function of `ctx.time.sim` plus
   * per-agent constants drawn from the seeded fork — there is no wall clock and no
   * rAF-driven state anywhere in this subsystem. Flag it and re-push.
   */
  freezeClocks() {
    this._frozen = true;
    if (this._built) this._present();
  }

  /** Full deterministic rebuild. §7.2b: re-fork, do not reuse the init() fork. */
  async reset(opts) {
    void opts;
    this.rng = this.ctx.rng.fork(T.rngLabel);
    this._teardown();
    await this._build();
    this._publish();
  }

  _teardown() {
    this._built = false;
    if (this.parked) { this.parked.dispose(); this.parked = null; }
    if (this.rig) { this.rig.dispose(); this.rig = null; }
    if (this.models) { disposeModels(this.models); this.models = null; }
    this.fleet = null;
    this.graph = null;
  }

  dispose() {
    for (const off of this._unsub) { try { off(); } catch { /* already gone */ } }
    this._unsub.length = 0;
    this._teardown();
    if (this.ctx.traffic === this.query) this.ctx.traffic = null;
    this.query = null;
  }
}

export default TrafficSystem;
